import { Link, NavLink, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useLang } from "@/contexts/LangContext";
import { CATEGORY_META, CATEGORY_ORDER, T, tx } from "@/lib/i18n";
import { Search, Menu, X, ChevronRight } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";

export function Header() {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const [q, setQ] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-slate-200" data-testid="site-header">
      <div className="bg-slate-900 text-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1.5 flex items-center justify-between text-[11px]">
          <span className="tracking-wide">
            {lang === "en"
              ? "Government of India notifications digest — Independent portal, not affiliated with any ministry."
              : "भारत सरकार अधिसूचना सार — स्वतंत्र पोर्टल, किसी मंत्रालय से संबद्ध नहीं।"}
          </span>
          <div className="flex items-center gap-1" role="group" aria-label="language switcher">
            <button
              data-testid="lang-toggle-en"
              onClick={() => setLang("en")}
              className={`px-2 py-0.5 rounded-sm transition-colors ${lang === "en" ? "bg-white text-slate-900 font-semibold" : "hover:bg-white/10"}`}
              aria-label="Switch to English"
            >EN</button>
            <button
              data-testid="lang-toggle-hi"
              onClick={() => setLang("hi")}
              className={`px-2 py-0.5 rounded-sm transition-colors ${lang === "hi" ? "bg-white text-slate-900 font-semibold" : "hover:bg-white/10"}`}
              aria-label="Switch to Hindi"
            >हिं</button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-6">
        <Logo />
        <form onSubmit={submit} className="hidden md:flex flex-1 max-w-xl ml-auto relative" role="search">
          <input
            data-testid="header-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={tx("ui.search_placeholder", lang)}
            className="w-full h-11 pl-11 pr-4 border border-slate-300 rounded-md text-sm outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 transition-colors"
          />
          <Search className="absolute left-3.5 top-3 text-slate-400" size={18} />
        </form>
        <Link
          to="/admin/login"
          data-testid="admin-link"
          className="hidden md:inline-flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors"
        >
          {tx("nav.admin", lang)} <ChevronRight size={14} />
        </Link>
        <button
          className="md:hidden p-2 -mr-2"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <nav className="hidden md:block border-t border-slate-200 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-1 overflow-x-auto">
          {CATEGORY_ORDER.map((slug) => (
            <NavLink
              key={slug}
              to={`/${slug}`}
              data-testid={`nav-${slug}`}
              className={({ isActive }) =>
                `px-3.5 py-3 text-[13px] font-medium whitespace-nowrap border-b-2 transition-colors ${
                  isActive
                    ? "text-slate-900 border-orange-600"
                    : "text-slate-600 border-transparent hover:text-slate-900 hover:border-slate-300"
                }`
              }
            >
              {CATEGORY_META[slug][lang === "hi" ? "short_hi" : "short_en"]}
            </NavLink>
          ))}
        </div>
      </nav>

      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
          <form onSubmit={submit} className="p-3 border-b border-slate-100 relative">
            <input
              data-testid="mobile-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={tx("ui.search_placeholder", lang)}
              className="w-full h-11 pl-10 pr-3 border border-slate-300 rounded-md text-sm"
            />
            <Search className="absolute left-6 top-6 text-slate-400" size={18} />
          </form>
          <div className="grid grid-cols-2 gap-px bg-slate-100">
            {CATEGORY_ORDER.map((slug) => (
              <Link
                key={slug}
                to={`/${slug}`}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${slug}`}
                className="bg-white px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                {CATEGORY_META[slug][lang === "hi" ? "short_hi" : "short_en"]}
              </Link>
            ))}
          </div>
          <div className="p-3">
            <Button asChild variant="outline" className="w-full" data-testid="mobile-admin-btn">
              <Link to="/admin/login" onClick={() => setOpen(false)}>
                {tx("nav.admin", lang)}
              </Link>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const { lang } = useLang();
  return (
    <footer className="mt-16 bg-slate-900 text-slate-300" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid gap-10 md:grid-cols-4">
        <div className="md:col-span-2">
          <Logo inverse />
          <p className="mt-4 text-sm text-slate-400 max-w-md leading-relaxed">
            {tx("tagline", lang) || T.tagline[lang]}
          </p>
          <p className="mt-4 text-xs text-slate-500 leading-relaxed">
            {lang === "hi"
              ? "यह वेबसाइट भारत सरकार से संबद्ध नहीं है। कृपया सभी सूचना की पुष्टि आधिकारिक स्रोत से करें।"
              : "This portal is not affiliated with the Government of India. Please verify all information from official sources before applying."}
          </p>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.14em] text-slate-500 mb-3">
            {lang === "hi" ? "श्रेणियाँ" : "Categories"}
          </div>
          <ul className="space-y-2 text-sm">
            {CATEGORY_ORDER.slice(0, 6).map((s) => (
              <li key={s}>
                <Link to={`/${s}`} className="hover:text-white transition-colors">
                  {CATEGORY_META[s][lang === "hi" ? "short_hi" : "short_en"]}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.14em] text-slate-500 mb-3">
            {lang === "hi" ? "जानकारी" : "Information"}
          </div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="hover:text-white transition-colors">{tx("nav.about", lang)}</Link></li>
            <li><Link to="/contact" className="hover:text-white transition-colors">{tx("nav.contact", lang)}</Link></li>
            <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
            <li><Link to="/disclaimer" className="hover:text-white transition-colors">Disclaimer</Link></li>
            <li><Link to="/terms" className="hover:text-white transition-colors">Terms & Conditions</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 text-xs text-slate-500 flex flex-wrap justify-between gap-2">
          <span>© {new Date().getFullYear()} Naukri Suchna — Independent Career Portal</span>
          <span>{lang === "hi" ? "भारत में निर्मित" : "Built in India"}</span>
        </div>
      </div>
    </footer>
  );
}

export function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
